// src/pages/InsuranceRequest.jsx (version avec contrôle granulaire des données - COLONNES CORRIGÉES)
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { useTranslation } from "react-i18next";

export default function InsuranceRequest() {
  const navigate = useNavigate();
  const { artworkId } = useParams();
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage === "de" ? "de-DE" : i18n.resolvedLanguage === "en" ? "en-GB" : "fr-FR";
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [artwork, setArtwork] = useState(null);

  // Champs form
  const [insuredValue, setInsuredValue] = useState("");
  // ✅ setRequestType n'est jamais utilisé : on garde la valeur sans setter
  const [requestType] = useState("insurance_quote");

  // Contexte métier assurance
  const [coverageContext, setCoverageContext] = useState("home_premium");
  const [policyKind, setPolicyKind] = useState("");

  const [transportNeeded, setTransportNeeded] = useState(false);
  const [eventStartDate, setEventStartDate] = useState("");
  const [eventEndDate, setEventEndDate] = useState("");
  const [storageLocation, setStorageLocation] = useState("");

  const [context, setContext] = useState("");
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");

  const [acceptInsuranceTerms, setAcceptInsuranceTerms] = useState(false);

  // Contrôle granulaire des champs partagés
  const [dataSharing, setDataSharing] = useState({
    // Identité œuvre
    title: true,
    artist: true,
    year: true,
    typeOfArt: true,

    // Caractéristiques techniques
    medium: true,
    dimensions: true,

    // État et conservation
    condition: true,
    conditionNotes: true,

    // Authenticité (sensibles)
    provenance: false, // par défaut OFF pour confidentialité
    certificate_auth: true,
    signature: true,
    signature_desc: true,
    editionInfo: true,

    // Valeurs (très sensibles)
    acquisition_date: false, // par défaut OFF
    acquisition_source: false, // par défaut OFF
    acquisition_price: false, // par défaut OFF
    acquisition_type: false,
    estimated_value: true,
    valuation_estimated_value: true,
    last_estimation_date: true,

    // Localisation
    location_id: false, // par défaut OFF pour sécurité

    // Visuel
    image_url: true,

    // Autres
    description: false,
    documents: false,
    specifications: false,
    subjectMatter: false,
    styles: false,
  });

  // Options d'anonymisation intelligente
  // ✅ setAnonymize n'était jamais utilisé : il est utilisé dans le résumé / payload
  // On garde le state complet car le payload inclut anonymize + le résumé l’utilise.
  const [anonymize] = useState({
    title: false,
    provenance: true,
    acquisition_source: true,
    location_id: true,
  });

  const [showDataControl, setShowDataControl] = useState(false);

  // Load artwork avec les VRAIS noms de colonnes
  useEffect(() => {
    (async () => {
      if (!artworkId) return;
      try {
        const { data, error } = await supabase
          .from("artworks")
          .select(`
            id, title, artist, year, medium, dimensions, image_url,
            typeOfArt, condition, conditionNotes, provenance,
            certificate_auth, signature, signature_desc, editionInfo,
            acquisition_date, acquisition_source, acquisition_price, 
            acquisition_type, currency, estimated_value, 
            valuation_estimated_value, last_estimation_date,
            location_id, description, documents, specifications,
            subjectMatter, styles
          `)
          .eq("id", artworkId)
          .maybeSingle();
        if (error) throw error;
        setArtwork(data || null);

        if (data) {
          // Pré-remplir la valeur avec estimated_value ou valuation_estimated_value
          if (data.valuation_estimated_value) {
            setInsuredValue(String(data.valuation_estimated_value));
          } else if (data.estimated_value) {
            setInsuredValue(String(data.estimated_value));
          }
        }
      } catch (e) {
        console.warn("[InsuranceRequest] artwork load failed:", e);
      }
    })();
  }, [artworkId]);

  // Fonction helper : anonymiser une valeur selon le contexte
  // ✅ useCallback + deps pour éviter les warnings et stabiliser le hook
  const anonymizeValue = useCallback(
    (fieldName, value) => {
      if (!value || !anonymize[fieldName]) return value;

      switch (fieldName) {
        case "title":
          return artwork?.artist ? t("insuranceRequest.anonymized.workByArtist", { artist: artwork.artist }) : value;

        case "provenance":
          if (typeof value === "string" && value.length > 100) {
            return t("insuranceRequest.anonymized.longProvenance");
          }
          return t("insuranceRequest.anonymized.establishedProvenance");

        case "acquisition_source":
          if (typeof value === "string") {
            const v = value.toLowerCase();
            if (v.includes("galerie") || v.includes("gallery")) {
              return t("insuranceRequest.anonymized.specializedGallery");
            }
            if (v.includes("enchères") || v.includes("auction")) {
              return t("insuranceRequest.anonymized.publicAuction");
            }
          }
          return t("insuranceRequest.anonymized.professionalSource");

        case "location_id":
          // On ne peut pas anonymiser un ID, on retourne juste un placeholder
          return t("insuranceRequest.anonymized.secureLocation");

        default:
          return value;
      }
    },
    [anonymize, artwork, t]
  );

  // Construire le payload en fonction des choix de partage
  const payload = useMemo(() => {
    const buildArtworkData = () => {
      if (!artwork) return null;

      const sharedData = {};

      Object.keys(dataSharing).forEach((field) => {
        if (dataSharing[field] && artwork[field] !== undefined && artwork[field] !== null) {
          sharedData[field] = anonymizeValue(field, artwork[field]);
        }
      });

      sharedData.id = artwork.id;

      if (anonymize.title && !artwork.artist && dataSharing.title) {
        sharedData.title = artwork.title;
      }

      return sharedData;
    };

    return {
      artwork: buildArtworkData(),
      client: {
        name: clientName || null,
        email: clientEmail || null,
      },
      insurance: {
        coverage_context: coverageContext || null,
        policy_kind: coverageContext === "fine_art_policy" ? policyKind || null : null,
        transport_needed: !!transportNeeded,
        event_start_date: eventStartDate || null,
        event_end_date: eventEndDate || null,
        storage_location: storageLocation || null,
      },
      consent: {
        insurance_terms_accepted: !!acceptInsuranceTerms,
        insurance_terms_version: "v1.0",
        accepted_at: acceptInsuranceTerms ? new Date().toISOString() : null,
      },
      data_sharing_preferences: dataSharing,
      anonymization_applied: anonymize,
      context: context || null,
    };
    // ✅ Fix ESLint: anonymizeValue est utilisée ici, elle doit être en deps.
  }, [
    artwork,
    dataSharing,
    anonymize,
    anonymizeValue,
    clientEmail,
    clientName,
    context,
    coverageContext,
    policyKind,
    transportNeeded,
    eventStartDate,
    eventEndDate,
    storageLocation,
    acceptInsuranceTerms,
  ]);

  const resetEventFields = () => {
    setTransportNeeded(false);
    setEventStartDate("");
    setEventEndDate("");
  };

  const selectAll = () => {
    const allTrue = {};
    Object.keys(dataSharing).forEach((k) => {
      allTrue[k] = true;
    });
    setDataSharing(allTrue);
  };

  const selectMinimal = () => {
    setDataSharing({
      title: false,
      artist: true,
      year: true,
      typeOfArt: true,
      medium: true,
      dimensions: true,
      condition: true,
      conditionNotes: false,
      provenance: false,
      certificate_auth: true,
      signature: true,
      signature_desc: false,
      editionInfo: true,
      acquisition_date: false,
      acquisition_source: false,
      acquisition_price: false,
      acquisition_type: false,
      estimated_value: true,
      valuation_estimated_value: true,
      last_estimation_date: false,
      location_id: false,
      image_url: false,
      description: false,
      documents: false,
      specifications: false,
      subjectMatter: false,
      styles: false,
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");

    try {
      setLoading(true);

      if (!acceptInsuranceTerms) {
        throw new Error(t("insuranceRequest.errors.acceptTerms"));
      }

      const { data: uRes, error: uErr } = await supabase.auth.getUser();
      if (uErr) throw uErr;
      const user = uRes?.user;
      if (!user?.id) throw new Error(t("insuranceRequest.errors.noSession"));

      const insuredValueNum = insuredValue === "" ? null : Number(String(insuredValue).replace(",", "."));
      if (insuredValueNum !== null && Number.isNaN(insuredValueNum)) {
        throw new Error(t("insuranceRequest.errors.invalidInsuredValue"));
      }

      if (coverageContext === "fine_art_policy" && !policyKind) {
        throw new Error(t("insuranceRequest.errors.selectPolicy"));
      }

      const eventMode = coverageContext === "fine_art_policy" && policyKind === "nail_to_nail";

      const { data, error } = await supabase
        .from("insurance_requests")
        .insert([
          {
            created_by: user.id,
            artwork_id: artworkId || null,
            partner_id: null,
            status: "pending",
            request_type: requestType,
            insured_value: insuredValueNum,
            currency: "EUR",
            coverage_context: coverageContext,
            policy_kind: coverageContext === "fine_art_policy" ? policyKind : null,
            event_mode: !!eventMode,
            event_start_date: eventMode && eventStartDate ? eventStartDate : null,
            event_end_date: eventMode && eventEndDate ? eventEndDate : null,
            transport_needed: !!transportNeeded,
            storage_location: storageLocation || null,
            payload: payload,
          },
        ])
        .select("id")
        .single();

      if (error) throw error;

      navigate(`/insurance/requests/${data.id}`);
    } catch (e2) {
      console.error(e2);
      setErr(e2?.message || t("insuranceRequest.errors.submit"));
    } finally {
      setLoading(false);
    }
  };

  const sharedFieldsCount = Object.values(dataSharing).filter(Boolean).length;
  const totalFields = Object.keys(dataSharing).length;

  return (
    <div className="p-6 md:p-8 max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-6">
        <div className="flex items-start gap-4">
          <div className="flex-1">
            <div className="text-2xl md:text-3xl font-serif font-semibold text-gray-900">{t("insuranceRequest.header.title")}</div>
            <div className="text-gray-700 mt-2">
              {t("insuranceRequest.header.subtitle")}
            </div>
          </div>
          <div className="text-4xl">🛡️</div>
        </div>
      </div>

      {/* Bloc CRITIQUE : Contrôle des données */}
      {artworkId && artwork && (
        <div className="rounded-2xl border border-blue-200 bg-gradient-to-br from-blue-50 to-white p-6 space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <span>🔒</span>
                <span>{t("insuranceRequest.dataControl.title")}</span>
              </div>
              <div className="text-sm text-gray-700">
                {t("insuranceRequest.dataControl.description")}
              </div>
              <div className="mt-3 flex items-center gap-2 text-sm">
                <div className="px-3 py-1.5 rounded-lg bg-white border border-gray-200">
                  <span className="font-semibold text-gray-900">{sharedFieldsCount}</span>
                  <span className="text-gray-600">{t("insuranceRequest.dataControl.sharedCountSuffix", { total: totalFields })}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 pt-2">
            <button
              type="button"
              onClick={() => setShowDataControl(!showDataControl)}
              className="px-4 py-2 rounded-xl bg-white border border-gray-300 hover:bg-gray-50 transition-all text-sm font-medium"
            >
              {showDataControl ? t("insuranceRequest.dataControl.hide") : t("insuranceRequest.dataControl.customize")} {t("insuranceRequest.dataControl.controlLabel")}
            </button>
            <button
              type="button"
              onClick={selectAll}
              className="px-4 py-2 rounded-xl bg-white border border-gray-200 hover:bg-gray-50 transition-all text-sm"
            >
              {t("insuranceRequest.dataControl.shareAll")}
            </button>
            <button
              type="button"
              onClick={selectMinimal}
              className="px-4 py-2 rounded-xl bg-white border border-gray-200 hover:bg-gray-50 transition-all text-sm"
            >
              {t("insuranceRequest.dataControl.minimum")}
            </button>
          </div>

          {/* Panneau de contrôle granulaire - VERSION SIMPLIFIÉE */}
          {showDataControl && (
            <div className="mt-4 rounded-xl border border-gray-200 bg-white p-5 space-y-5">
              {/* Section Identité */}
              <div>
                <div className="text-sm font-semibold text-gray-900 mb-3">{t("insuranceRequest.sections.identity")}</div>
                <div className="grid md:grid-cols-2 gap-3">
                  {["title", "artist", "year", "typeOfArt"].map((field) => (
                    <label key={field} className="flex items-center gap-2.5 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={dataSharing[field]}
                        onChange={(e) => setDataSharing({ ...dataSharing, [field]: e.target.checked })}
                        className="w-4 h-4"
                      />
                      <span className="text-gray-700">{t(`insuranceRequest.fields.${field}`)}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Section Technique */}
              <div className="pt-4 border-t border-gray-200">
                <div className="text-sm font-semibold text-gray-900 mb-3">
                  {t("insuranceRequest.sections.technical")}
                  <span className="text-xs font-normal text-amber-600 ml-2">{t("insuranceRequest.labels.recommended")}</span>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  {["medium", "dimensions"].map((field) => (
                    <label key={field} className="flex items-center gap-2.5 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={dataSharing[field]}
                        onChange={(e) => setDataSharing({ ...dataSharing, [field]: e.target.checked })}
                        className="w-4 h-4"
                      />
                      <span className="text-gray-700">{t(`insuranceRequest.fields.${field}`)}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Section État */}
              <div className="pt-4 border-t border-gray-200">
                <div className="text-sm font-semibold text-gray-900 mb-3">
                  {t("insuranceRequest.sections.condition")}
                  <span className="text-xs font-normal text-amber-600 ml-2">{t("insuranceRequest.labels.premiumImpact")}</span>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  {["condition", "conditionNotes"].map((field) => (
                    <label key={field} className="flex items-center gap-2.5 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={dataSharing[field]}
                        onChange={(e) => setDataSharing({ ...dataSharing, [field]: e.target.checked })}
                        className="w-4 h-4"
                      />
                      <span className="text-gray-700">{t(`insuranceRequest.fields.${field}`)}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Section Authenticité */}
              <div className="pt-4 border-t border-gray-200">
                <div className="text-sm font-semibold text-gray-900 mb-3">
                  {t("insuranceRequest.sections.authenticity")}
                  <span className="text-xs font-normal text-red-600 ml-2">{t("insuranceRequest.labels.sensitive")}</span>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  {["provenance", "certificate_auth", "signature", "signature_desc", "editionInfo"].map((field) => (
                    <label key={field} className="flex items-center gap-2.5 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={dataSharing[field]}
                        onChange={(e) => setDataSharing({ ...dataSharing, [field]: e.target.checked })}
                        className="w-4 h-4"
                      />
                      <span className="text-gray-700">{t(`insuranceRequest.fields.${field}`)}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Section Valeurs */}
              <div className="pt-4 border-t border-gray-200">
                <div className="text-sm font-semibold text-gray-900 mb-3">
                  {t("insuranceRequest.sections.values")}
                  <span className="text-xs font-normal text-red-600 ml-2">{t("insuranceRequest.labels.verySensitive")}</span>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  {["estimated_value", "valuation_estimated_value", "last_estimation_date"].map((field) => (
                    <label key={field} className="flex items-center gap-2.5 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={dataSharing[field]}
                        onChange={(e) => setDataSharing({ ...dataSharing, [field]: e.target.checked })}
                        className="w-4 h-4"
                      />
                      <span className="text-gray-700 text-xs">{t(`insuranceRequest.fields.${field}`)}</span>
                    </label>
                  ))}
                </div>

                <div className="rounded-lg bg-red-50 border border-red-100 p-3 mt-3">
                  <div className="text-xs font-medium text-red-900 mb-2">{t("insuranceRequest.sections.acquisitionWarning")}</div>
                  <div className="grid md:grid-cols-2 gap-2">
                    {["acquisition_price", "acquisition_source", "acquisition_date", "acquisition_type"].map((field) => (
                      <label key={field} className="flex items-center gap-2 text-xs cursor-pointer">
                        <input
                          type="checkbox"
                          checked={dataSharing[field]}
                          onChange={(e) => setDataSharing({ ...dataSharing, [field]: e.target.checked })}
                          className="w-3 h-3"
                        />
                        <span className="text-gray-700">{t(`insuranceRequest.fields.${field}`)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Section Autres */}
              <div className="pt-4 border-t border-gray-200">
                <div className="text-sm font-semibold text-gray-900 mb-3">{t("insuranceRequest.sections.other")}</div>
                <div className="grid md:grid-cols-2 gap-3">
                  {["image_url", "description", "documents", "specifications", "subjectMatter", "styles", "location_id"].map(
                    (field) => (
                      <label key={field} className="flex items-center gap-2.5 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={dataSharing[field]}
                          onChange={(e) => setDataSharing({ ...dataSharing, [field]: e.target.checked })}
                          className="w-4 h-4"
                        />
                        <span className="text-gray-700">{t(`insuranceRequest.fields.${field}`)}</span>
                      </label>
                    )
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Résumé des anonymisations actives */}
          {Object.values(anonymize).some(Boolean) && (
            <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-xs text-gray-700">
              🔐 <strong>{t("insuranceRequest.anonymization.active")}</strong>{" "}
              {Object.entries(anonymize)
                .filter(([key, value]) => value && dataSharing[key])
                .map(([key]) => t(`insuranceRequest.fields.${key}`))
                .join(", ")}
            </div>
          )}
        </div>
      )}

      {err && <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{err}</div>}

      {/* Formulaire principal */}
      <form onSubmit={submit} className="rounded-2xl border border-gray-200 bg-white p-6 space-y-5">
        {/* Récap œuvre sélectionnée */}
        {artwork ? (
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-2">
            <div className="font-medium text-gray-900 text-base">
              {artwork.title || t("insuranceRequest.artwork.fallbackTitle")}{" "}
              {artwork.artist ? <span className="text-gray-500">• {artwork.artist}</span> : null}
            </div>
            <div className="text-sm text-gray-600 space-y-1">
              {artwork.year && <div>📅 {t("insuranceRequest.artwork.year")}: {artwork.year}</div>}
              {artwork.medium && <div>🎨 {t("insuranceRequest.artwork.medium")}: {artwork.medium}</div>}
              {artwork.dimensions && <div>📏 {t("insuranceRequest.artwork.dimensions")}: {artwork.dimensions}</div>}
              {artwork.condition && <div>⚙️ {t("insuranceRequest.artwork.condition")}: {artwork.condition}</div>}
              {artwork.certificate_auth && <div>✅ {t("insuranceRequest.artwork.certificate")}: {artwork.certificate_auth}</div>}
              {artwork.estimated_value && <div>💰 {t("insuranceRequest.artwork.currentEstimate")}: {Number(artwork.estimated_value).toLocaleString(locale)} EUR</div>}
            </div>
            <div className="text-xs text-gray-600 mt-2 pt-2 border-t border-gray-200">
              📊 {t("insuranceRequest.artwork.sharedSummary", { shared: sharedFieldsCount, total: totalFields })}
            </div>
          </div>
        ) : (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-gray-700">
            💡 <strong>{t("insuranceRequest.artwork.tipTitle")}</strong> {t("insuranceRequest.artwork.tipBody")}
          </div>
        )}

        {/* Contexte assurance (métier) */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">{t("insuranceRequest.coverage.question")}</label>
          <div className="grid md:grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => {
                setCoverageContext("home_premium");
                setPolicyKind("");
                resetEventFields();
              }}
              className={[
                "p-4 rounded-xl border text-left transition-all",
                coverageContext === "home_premium" ? "border-black bg-gray-50" : "border-gray-200 bg-white hover:bg-gray-50",
              ].join(" ")}
            >
              🏠 <span className="font-medium">{t("insuranceRequest.coverage.homePremium.title")}</span>
              <div className="text-xs text-gray-600 mt-1">
                {t("insuranceRequest.coverage.homePremium.body")}
              </div>
            </button>

            <button
              type="button"
              onClick={() => {
                setCoverageContext("fine_art_policy");
                resetEventFields();
              }}
              className={[
                "p-4 rounded-xl border text-left transition-all",
                coverageContext === "fine_art_policy" ? "border-black bg-gray-50" : "border-gray-200 bg-white hover:bg-gray-50",
              ].join(" ")}
            >
              🖼️ <span className="font-medium">{t("insuranceRequest.coverage.fineArt.title")}</span>
              <div className="text-xs text-gray-600 mt-1">
                {t("insuranceRequest.coverage.fineArt.body")}
              </div>
            </button>
          </div>
        </div>

        {/* Type de police (si contrat spécifique) */}
        {coverageContext === "fine_art_policy" ? (
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">{t("insuranceRequest.policy.label")}</label>
              <select
                className="mt-1 w-full rounded-xl border border-gray-200 p-2.5 text-sm"
                value={policyKind}
                onChange={(e) => {
                  const v = e.target.value;
                  setPolicyKind(v);
                  if (v !== "nail_to_nail") {
                    setEventStartDate("");
                    setEventEndDate("");
                  }
                }}
              >
                <option value="">{t("insuranceRequest.policy.select")}</option>
                <option value="nail_to_nail">{t("insuranceRequest.policy.nailToNail")}</option>
                <option value="all_risks">{t("insuranceRequest.policy.allRisks")}</option>
              </select>
            </div>

            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={transportNeeded}
                  onChange={(e) => setTransportNeeded(e.target.checked)}
                  className="w-4 h-4"
                />
                <span>{t("insuranceRequest.policy.transportRequired")}</span>
              </label>
            </div>
          </div>
        ) : null}

        {/* Champs spécifiques clou à clou */}
        {coverageContext === "fine_art_policy" && policyKind === "nail_to_nail" ? (
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">{t("insuranceRequest.event.start")}</label>
              <input
                type="date"
                className="mt-1 w-full rounded-xl border border-gray-200 p-2 text-sm"
                value={eventStartDate}
                onChange={(e) => setEventStartDate(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">{t("insuranceRequest.event.end")}</label>
              <input
                type="date"
                className="mt-1 w-full rounded-xl border border-gray-200 p-2 text-sm"
                value={eventEndDate}
                onChange={(e) => setEventEndDate(e.target.value)}
              />
            </div>
          </div>
        ) : null}

        {/* {t("insuranceRequest.form.storageLocation")} */}
        <div>
          <label className="block text-sm font-medium text-gray-700">{t("insuranceRequest.form.storageLocation")}</label>
          <input
            className="mt-1 w-full rounded-xl border border-gray-200 p-2.5 text-sm"
            value={storageLocation}
            onChange={(e) => setStorageLocation(e.target.value)}
            placeholder={t("insuranceRequest.form.storagePlaceholder")}
          />
        </div>

        {/* Valeur */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            {t("insuranceRequest.form.insuredValue")}{" "}
            {(artwork?.valuation_estimated_value || artwork?.estimated_value) && t("insuranceRequest.form.prefilled")}
          </label>
          <input
            className="mt-1 w-full rounded-xl border border-gray-200 p-2.5 text-sm"
            value={insuredValue}
            onChange={(e) => setInsuredValue(e.target.value)}
            placeholder={t("insuranceRequest.form.valuePlaceholder")}
            inputMode="decimal"
          />
        </div>

        {/* Contexte / message */}
        <div>
          <label className="block text-sm font-medium text-gray-700">{t("insuranceRequest.form.context")}</label>
          <textarea
            className="mt-1 w-full rounded-xl border border-gray-200 p-3 min-h-[110px] text-sm"
            value={context}
            onChange={(e) => setContext(e.target.value)}
            placeholder={t("insuranceRequest.form.contextPlaceholder")}
          />
        </div>

        {/* Coordonnées optionnelles */}
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">{t("insuranceRequest.form.name")}</label>
            <input
              className="mt-1 w-full rounded-xl border border-gray-200 p-2.5 text-sm"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t("insuranceRequest.form.email")}</label>
            <input
              className="mt-1 w-full rounded-xl border border-gray-200 p-2.5 text-sm"
              value={clientEmail}
              onChange={(e) => setClientEmail(e.target.value)}
              type="email"
            />
          </div>
        </div>

        {/* Consentement */}
        <div className="rounded-2xl border border-gray-200 bg-gray-50 p-5 space-y-3">
          <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
            <span>🛡️</span>
            <span>{t("insuranceRequest.consent.title")}</span>
          </div>
          <div className="text-xs text-gray-700">
            {t("insuranceRequest.consent.description")}
          </div>

          <label className="flex items-start gap-2.5 text-xs text-gray-700 mt-3 cursor-pointer">
            <input
              type="checkbox"
              checked={acceptInsuranceTerms}
              onChange={(e) => setAcceptInsuranceTerms(e.target.checked)}
              className="mt-0.5 w-4 h-4"
            />
            <span>
              {t("insuranceRequest.consent.checkbox")}
              <a
                href="/legal/insurance-terms"
                target="_blank"
                rel="noopener noreferrer"
                className="ml-1 underline text-gray-900 font-medium"
              >
                {t("insuranceRequest.consent.link")}
              </a>
            </span>
          </label>
        </div>

        <div className="flex gap-3 pt-2">
          <button
            type="submit"
            disabled={!acceptInsuranceTerms || loading}
            className="px-5 py-2.5 rounded-xl bg-black text-white font-medium disabled:opacity-50 hover:bg-gray-800 transition-all"
          >
            {loading ? t("insuranceRequest.actions.sending") : t("insuranceRequest.actions.submit")}
          </button>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-5 py-2.5 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 transition-all"
          >
            {t("insuranceRequest.actions.cancel")}
          </button>
        </div>
      </form>

      {/* Footer pédagogique */}
      <div className="rounded-2xl border border-purple-100 bg-gradient-to-br from-purple-50 to-white p-5">
        <div className="flex items-start gap-3">
          <div className="text-2xl">💡</div>
          <div className="text-sm text-gray-700 space-y-1.5">
            <p className="font-medium text-gray-900">{t("insuranceRequest.footer.title")}</p>
            <p>{t("insuranceRequest.footer.body")}</p>
            <p className="text-xs text-gray-600 italic">{t("insuranceRequest.footer.note")}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
